import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Cpu,
  Printer,
  Zap,
  Lock,
  CheckCircle,
  ChevronRight,
  ArrowRight,
  Shield,
} from 'lucide-react';

// ─── Module definitions ──────────────────────────────────────────────────────

const MODULES = [
  {
    id: 'cnc_routing',
    label: 'CNC Router',
    description: 'Feeds & speeds, tooling, troubleshooting',
    icon: Cpu,
    accessKey: 'cnc_routing',
  },
  {
    id: '3d_printing',
    label: '3D Printing',
    description: 'Slicer settings, material guides, troubleshooting',
    icon: Printer,
    accessKey: '3d_printing',
  },
  {
    id: 'laser_cutting',
    label: 'Laser Cutting',
    description: 'Power & speed settings, materials, safety',
    icon: Zap,
    accessKey: 'laser_cutting',
  },
];

const STEP_LABELS = ['Module', 'Machine', 'Profile', 'Ready'];

const SAFETY_CHECKLIST = [
  'Workpiece secured to bed',
  'Tool seated and collet tightened',
  'Z-zero set at material surface',
  'Dust extraction connected and running',
  'Safety glasses on',
];

// ─── Component ───────────────────────────────────────────────────────────────

const OnboardingModal = ({ onComplete }) => {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');

  // Which modules this user can access — default CNC-only if billing not yet implemented
  const disciplineAccess = user?.discipline_access ?? { cnc_routing: true, '3d_printing': false, laser_cutting: false };

  const firstUnlocked = MODULES.find(m => disciplineAccess[m.accessKey])?.id ?? 'cnc_routing';

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [selectedModule, setSelectedModule] = useState(firstUnlocked);
  const [machineData, setMachineData] = useState({
    machine_class: 'hobby',
    spindle_type: 'trim_router',
    max_rpm: 24000,
    units: 'metric',
  });
  const [profileData, setProfileData] = useState({
    experience_level: 'beginner',
    safety_style: 'conservative',
  });

  const progress = Math.round(((step - 1) / (STEP_LABELS.length - 1)) * 100);

  const selectedModuleLabel = MODULES.find(m => m.id === selectedModule)?.label ?? 'CNC Router';

  // ── Helpers ──────────────────────────────────────────────────────────────

  const handleModuleClick = (mod) => {
    const isUnlocked = disciplineAccess[mod.accessKey];
    if (!isUnlocked) {
      toast.error('This module requires an upgrade. Contact us to add it.');
      return;
    }
    setSelectedModule(mod.id);
  };

  const handleComplete = async () => {
    setSubmitting(true);
    try {
      // 1. Save onboarding profile data to /api/onboarding
      await axios.post(
        `${API}/onboarding`,
        {
          ...machineData,
          ...profileData,
          selected_module: selectedModule,
          completed: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // 2. Mark onboarding_complete on the user record
      await axios.patch(
        `${API}/users/me/onboarding-complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onComplete();
    } catch (err) {
      console.error('Onboarding completion failed:', err);
      toast.error('Failed to save setup. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  const renderProgressBar = () => (
    <div className="mb-6">
      {/* Progress bar */}
      <div className="w-full h-1.5 bg-[#2a2a2a] rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-[#e67e22] rounded-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>
      {/* Step tabs */}
      <div className="flex gap-1">
        {STEP_LABELS.map((label, i) => {
          const stepNum = i + 1;
          const isActive = stepNum === step;
          const isDone = stepNum < step;
          return (
            <div
              key={label}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-xs font-semibold transition-colors ${
                isActive
                  ? 'bg-[#e67e22]/20 text-[#e67e22] border border-[#e67e22]/40'
                  : isDone
                  ? 'bg-black/40 text-[#10b981] border border-[#10b981]/30'
                  : 'bg-black/30 text-[#6b7280] border border-[#3a3a3a]/50'
              }`}
            >
              {isDone ? (
                <CheckCircle className="w-3 h-3" />
              ) : (
                <span className="w-4 h-4 rounded-full border border-current flex items-center justify-center text-[10px]">
                  {stepNum}
                </span>
              )}
              <span className="hidden sm:inline">{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Step 1: Module selection ──────────────────────────────────────────────

  const renderStep1 = () => {
    const activeModuleLabel = MODULES.find(m => disciplineAccess[m.accessKey])?.label ?? 'CNC Router';
    return (
      <div className="space-y-5">
        {/* Subscription banner */}
        <div className="flex items-center gap-3 bg-[#e67e22]/10 border border-[#e67e22]/30 rounded-lg px-4 py-3">
          <Shield className="w-4 h-4 text-[#e67e22] flex-shrink-0" />
          <span className="text-sm text-[#e0e0e0]">
            Your subscription: <span className="font-semibold text-white">{activeModuleLabel}</span>
          </span>
          <Badge className="ml-auto bg-[#10b981]/20 text-[#10b981] border border-[#10b981]/30 text-xs">
            Active
          </Badge>
        </div>

        <p className="text-sm text-[#a3a3a3]">Select the module you want to work in:</p>

        {/* Module cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {MODULES.map((mod) => {
            const Icon = mod.icon;
            const isUnlocked = disciplineAccess[mod.accessKey];
            const isSelected = selectedModule === mod.id;
            return (
              <button
                key={mod.id}
                type="button"
                onClick={() => handleModuleClick(mod)}
                className={`relative text-left p-4 rounded-lg border transition-all ${
                  !isUnlocked
                    ? 'opacity-45 cursor-not-allowed border-[#3a3a3a]/50 bg-black/30'
                    : isSelected
                    ? 'border-[#e67e22] bg-[#e67e22]/10 cursor-pointer'
                    : 'border-[#3a3a3a]/50 bg-black/40 hover:border-[#e67e22]/50 cursor-pointer'
                }`}
              >
                {/* Lock badge for locked modules */}
                {!isUnlocked && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-[#3a3a3a] rounded-full px-1.5 py-0.5">
                    <Lock className="w-2.5 h-2.5 text-[#a3a3a3]" />
                    <span className="text-[10px] text-[#a3a3a3] font-semibold">Upgrade</span>
                  </div>
                )}
                {/* Selected checkmark */}
                {isSelected && isUnlocked && (
                  <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-[#e67e22]" />
                )}
                <Icon className={`w-6 h-6 mb-2 ${isSelected && isUnlocked ? 'text-[#e67e22]' : 'text-[#a3a3a3]'}`} />
                <div className={`text-sm font-semibold mb-0.5 ${isSelected && isUnlocked ? 'text-white' : 'text-[#d6d6d6]'}`}>
                  {mod.label}
                </div>
                <div className="text-xs text-[#6b7280] leading-snug">{mod.description}</div>
              </button>
            );
          })}
        </div>

        {/* Upgrade link */}
        <div className="text-center">
          <Link
            to="/pricing"
            className="text-xs text-[#a3a3a3] hover:text-[#e67e22] transition-colors inline-flex items-center gap-1"
          >
            Want access to all three modules? View upgrade options
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={() => setStep(2)}
            className="bg-[#e67e22] hover:bg-[#f39c12] text-black font-semibold"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  // ── Step 2: Machine setup ─────────────────────────────────────────────────

  const renderStep2 = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#a3a3a3]">Tell us about your machine so we can give accurate recommendations.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Machine type */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-[#a3a3a3] uppercase tracking-wide">Machine Type</label>
          <Select
            value={machineData.machine_class}
            onValueChange={v => setMachineData(p => ({ ...p, machine_class: v }))}
          >
            <SelectTrigger className="bg-[#1a1a1a] border-[#3a3a3a] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-[#3a3a3a]">
              <SelectItem value="hobby" className="text-white hover:bg-[#2a2a2a]">Hobby / Desktop</SelectItem>
              <SelectItem value="semi_professional" className="text-white hover:bg-[#2a2a2a]">Semi-Professional</SelectItem>
              <SelectItem value="professional" className="text-white hover:bg-[#2a2a2a]">Professional</SelectItem>
              <SelectItem value="industrial" className="text-white hover:bg-[#2a2a2a]">Industrial</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Spindle type */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-[#a3a3a3] uppercase tracking-wide">Spindle Type</label>
          <Select
            value={machineData.spindle_type}
            onValueChange={v => setMachineData(p => ({ ...p, spindle_type: v }))}
          >
            <SelectTrigger className="bg-[#1a1a1a] border-[#3a3a3a] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-[#3a3a3a]">
              <SelectItem value="trim_router" className="text-white hover:bg-[#2a2a2a]">Trim Router (e.g. Makita, DeWalt)</SelectItem>
              <SelectItem value="vfd_spindle" className="text-white hover:bg-[#2a2a2a]">VFD Spindle (water/air cooled)</SelectItem>
              <SelectItem value="servo_spindle" className="text-white hover:bg-[#2a2a2a]">Servo Spindle</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Max RPM */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-[#a3a3a3] uppercase tracking-wide">Max RPM</label>
          <Input
            type="number"
            value={machineData.max_rpm}
            onChange={e => setMachineData(p => ({ ...p, max_rpm: parseInt(e.target.value) || 0 }))}
            className="bg-[#1a1a1a] border-[#3a3a3a] text-white"
            min={1000}
            max={60000}
          />
        </div>

        {/* Units */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-[#a3a3a3] uppercase tracking-wide">Units</label>
          <Select
            value={machineData.units}
            onValueChange={v => setMachineData(p => ({ ...p, units: v }))}
          >
            <SelectTrigger className="bg-[#1a1a1a] border-[#3a3a3a] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-[#3a3a3a]">
              <SelectItem value="metric" className="text-white hover:bg-[#2a2a2a]">Metric (mm)</SelectItem>
              <SelectItem value="imperial" className="text-white hover:bg-[#2a2a2a]">Imperial (inches)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <Button
          variant="ghost"
          onClick={() => setStep(1)}
          className="text-[#a3a3a3] hover:text-white"
        >
          ← Back
        </Button>
        <Button
          onClick={() => setStep(3)}
          className="bg-[#e67e22] hover:bg-[#f39c12] text-black font-semibold"
        >
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  // ── Step 3: Experience profile ────────────────────────────────────────────

  const renderStep3 = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#a3a3a3]">Help us tailor the advice and safety guidance to your level.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Experience level */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-[#a3a3a3] uppercase tracking-wide">Experience Level</label>
          <Select
            value={profileData.experience_level}
            onValueChange={v => setProfileData(p => ({ ...p, experience_level: v }))}
          >
            <SelectTrigger className="bg-[#1a1a1a] border-[#3a3a3a] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-[#3a3a3a]">
              <SelectItem value="beginner" className="text-white hover:bg-[#2a2a2a]">Beginner — learning the basics</SelectItem>
              <SelectItem value="intermediate" className="text-white hover:bg-[#2a2a2a]">Intermediate — comfortable with setups</SelectItem>
              <SelectItem value="advanced" className="text-white hover:bg-[#2a2a2a]">Advanced — optimising feeds & speeds</SelectItem>
              <SelectItem value="expert" className="text-white hover:bg-[#2a2a2a]">Expert — production environment</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Safety setting */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-[#a3a3a3] uppercase tracking-wide">Safety Setting</label>
          <Select
            value={profileData.safety_style}
            onValueChange={v => setProfileData(p => ({ ...p, safety_style: v }))}
          >
            <SelectTrigger className="bg-[#1a1a1a] border-[#3a3a3a] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-[#3a3a3a]">
              <SelectItem value="conservative" className="text-white hover:bg-[#2a2a2a]">Conservative — extra safety margins</SelectItem>
              <SelectItem value="standard" className="text-white hover:bg-[#2a2a2a]">Standard — balanced performance</SelectItem>
              <SelectItem value="aggressive" className="text-white hover:bg-[#2a2a2a]">Aggressive — push material limits</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Static pre-run safety checklist */}
      <div className="bg-black/50 border border-[#3a3a3a]/60 rounded-lg p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-3 flex items-center gap-2">
          <Shield className="w-3.5 h-3.5" />
          Pre-Run Safety Checklist
        </div>
        <ul className="space-y-2">
          {SAFETY_CHECKLIST.map((item, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-[#d6d6d6]">
              <span className="w-4 h-4 rounded-full border border-[#e67e22]/40 bg-[#e67e22]/10 flex items-center justify-center flex-shrink-0">
                <span className="text-[9px] text-[#e67e22] font-bold">{i + 1}</span>
              </span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex justify-between pt-2">
        <Button
          variant="ghost"
          onClick={() => setStep(2)}
          className="text-[#a3a3a3] hover:text-white"
        >
          ← Back
        </Button>
        <Button
          onClick={() => setStep(4)}
          className="bg-[#e67e22] hover:bg-[#f39c12] text-black font-semibold"
        >
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  // ── Step 4: Confirmation ──────────────────────────────────────────────────

  const MACHINE_CLASS_LABELS = {
    hobby: 'Hobby / Desktop',
    semi_professional: 'Semi-Professional',
    professional: 'Professional',
    industrial: 'Industrial',
  };
  const SPINDLE_LABELS = {
    trim_router: 'Trim Router',
    vfd_spindle: 'VFD Spindle',
    servo_spindle: 'Servo Spindle',
  };

  const summaryTags = [
    { label: selectedModuleLabel, color: 'text-[#e67e22] border-[#e67e22]/40 bg-[#e67e22]/10' },
    { label: MACHINE_CLASS_LABELS[machineData.machine_class], color: 'text-white border-[#3a3a3a] bg-black/40' },
    { label: machineData.units === 'metric' ? 'Metric (mm)' : 'Imperial (in)', color: 'text-white border-[#3a3a3a] bg-black/40' },
    { label: `${profileData.safety_style.charAt(0).toUpperCase() + profileData.safety_style.slice(1)} Safety`, color: 'text-[#10b981] border-[#10b981]/40 bg-[#10b981]/10' },
    { label: `${profileData.experience_level.charAt(0).toUpperCase() + profileData.experience_level.slice(1)}`, color: 'text-white border-[#3a3a3a] bg-black/40' },
  ];

  const dashboardPath = selectedModule === '3d_printing' ? '/3d-printing' : selectedModule === 'laser_cutting' ? '/laser' : '/dashboard';
  const calculatorPath = selectedModule === '3d_printing' ? '/3d-printing/calculator' : selectedModule === 'laser_cutting' ? '/laser/calculator' : '/calculator';

  const renderStep4 = () => (
    <div className="space-y-5">
      {/* Operational heading */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#10b981]/20 border border-[#10b981]/40 flex items-center justify-center flex-shrink-0">
          <CheckCircle className="w-5 h-5 text-[#10b981]" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-white">Workspace is operational.</h2>
          <p className="text-sm text-[#a3a3a3]">Your profile is configured and ready to use.</p>
        </div>
      </div>

      {/* Summary tags */}
      <div className="flex flex-wrap gap-2">
        {summaryTags.map((tag, i) => (
          <span
            key={i}
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${tag.color}`}
          >
            {tag.label}
          </span>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <Button
          variant="outline"
          className="flex-1 border-[#3a3a3a] text-[#d6d6d6] hover:border-[#e67e22] hover:text-[#e67e22]"
          onClick={handleComplete}
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'What do I do first?'}
        </Button>
        <Button
          className="flex-1 bg-[#e67e22] hover:bg-[#f39c12] text-black font-semibold"
          onClick={handleComplete}
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Open Calculator'}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      <div className="flex justify-start pt-1">
        <Button
          variant="ghost"
          onClick={() => setStep(3)}
          className="text-[#a3a3a3] hover:text-white text-xs"
          disabled={submitting}
        >
          ← Back
        </Button>
      </div>
    </div>
  );

  // ── Main render ───────────────────────────────────────────────────────────

  const stepContent = [renderStep1, renderStep2, renderStep3, renderStep4][step - 1];

  const stepHeadings = [
    { title: 'Welcome to CNC Helpdesk', sub: 'Choose your module to get started.' },
    { title: 'Machine Setup', sub: 'Configure your machine profile.' },
    { title: 'Experience Profile', sub: 'Personalise your experience.' },
    { title: "You're all set", sub: 'Review your configuration.' },
  ];

  return (
    /* Full-screen overlay — cannot be dismissed */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}
    >
      {/* Modal card */}
      <div
        className="w-full max-w-xl bg-[#111111] border border-[#3a3a3a] rounded-xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-0">
          <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-1">First-Time Setup</div>
          <h1 className="text-xl font-bold text-white">{stepHeadings[step - 1].title}</h1>
          <p className="text-sm text-[#a3a3a3] mt-0.5 mb-4">{stepHeadings[step - 1].sub}</p>
          {renderProgressBar()}
        </div>

        {/* Step body */}
        <div className="px-6 pb-6">
          {stepContent()}
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
